import {
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpEvent,
  HttpErrorResponse,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Location } from "@angular/common";

import { MatDialog } from "@angular/material/dialog";

import { TranslateService } from "@ngx-translate/core";

import { Observable, throwError, BehaviorSubject, of } from "rxjs";
import { take, filter, catchError, switchMap, finalize, delay } from "rxjs/operators";
import { Store } from "@ngrx/store";
import { AppState } from "../store/reducers";
import * as MessagesActionTypes from "../store/actions/messages/message.actions";

import { LoginService } from "../shared/services";
import { SnackbarService } from "../shared/services/snackbar/snackbar.service";
import { AppSessionService } from "../shared/services/app-session/app-session.service";

import { SnackbarType } from "../models/snackbar/snackbar.model";
import { UserProfileCodes } from "../models/UserDetails/userProfileDTO";

@Injectable()
export class BasicAuthInterceptor implements HttpInterceptor {
  pendingRequests: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  isRefreshingToken: boolean = false;

  constructor(
    private router: Router,
    private loginService: LoginService,
    private location: Location,
    private store: Store<AppState>,
    private snackbarService: SnackbarService,
    private appSession: AppSessionService,
    private translate: TranslateService,
    private dialogRef: MatDialog
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // add authorization header with basic auth credentials if available
    return next.handle(this.addAuthenticationHeader(request)).pipe(
      catchError((error) => {
        if (request.headers.has('skip-error-interceptor')) {
          return throwError(error);
        }
        return this.validateErrors(request, next, error);
      })
    );
  }

  private validateErrors(request, next, error) {
    if (error instanceof HttpErrorResponse) {
      switch (error.status) {
        case 0:
          return this.handleServerDownError(request, next);
        case 401:
          if (request.url.includes("/oauth/token")) {
            return throwError("Couldn't refresh token");
          }
          return this.handle401Unauthorized(request, next, error);
        case 400:
          if (request.url.includes("/oauth/token") && location.pathname !== '/passwordRecovery') {
            this.router.navigateByUrl("/login");
          }
          return this.handleError(error);
        default:
          return this.handleError(error);
      }
    } else {
      return this.handleError(error);
    }
  }

  private handleError(err: any) {
    let errorMsg: string;
    if (err && err?.error && err?.error?.error && err?.error?.error?.errors && err?.error?.error?.errors.length) {
      errorMsg = err?.error?.error?.errors[0]?.split(':')[1]?.trim();
    } else if (err && err?.error && err?.error?.error_description) {
      errorMsg = err?.error.error_description;
    } else if (err && err?.error && err?.error?.message) {
      errorMsg = err.error.message;
    } else if (err && err?.message && err?.error?.error) {
      let errorUiMsg = err?.error?.error?.sourceCode;
      errorMsg = errorUiMsg;
    } else if (err && err?.message) {
      errorMsg = err.message;
    } else if (err && err?.statusText) {
      errorMsg = err.statusText;
    } else if (err) {
      errorMsg = err;
    } else {
      errorMsg = "Unknown error";
    }
    return throwError(errorMsg);
  }

  handleServerDownError(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.appSession.fallbackUrl) {
      return;
    }
    this.appSession.useFallback = true;
    const startIndex = request.url.indexOf('//');
    const endIndex = request.url.substring(startIndex + 2, request.url.length).indexOf('/');
    const updatedReq = request.clone({
      url: this.appSession.fallbackUrl + request.url.substring(startIndex + 2 + endIndex, request.url.length)
    });
    return of(null).pipe(
      delay(5000),
      switchMap(() => next.handle(this.addAuthenticationHeader(updatedReq))),
      catchError((error) => this.validateErrors(request, next, error))
    );
  }

  private handle401Unauthorized(
    request: HttpRequest<any>,
    next: HttpHandler,
    error: any
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.pendingRequests.next(null);

      return this.loginService.refreshToken().pipe(
        switchMap((user) => {
          this.pendingRequests.next(user.refresh_token);
          return next.handle(this.addAuthenticationHeader(request));
        }),
        catchError((error) => {
          this.pendingRequests.next("ERROR");
          if (!this?.location?.path()?.includes("login") && location.pathname !== '/passwordRecovery') {
            this.translate.get("Auth.Expired").subscribe((translatedTokenExpirationErr) => {
              this.snackbarService.show({
                message: translatedTokenExpirationErr,
                type: SnackbarType.ERROR,
              });
            });
            this.logout();
          }
          return this.handleError("Unable to refresh token - " + error);
        }),
        finalize(() => {
          this.isRefreshingToken = false;
        })
      );
    } else {
      return this.pendingRequests.pipe(
        filter((refreshToken) => refreshToken != null),
        take(1),
        switchMap((response) => {
          if (response === "ERROR") {
            return this.handleError(error);
          } else {
            return next.handle(this.addAuthenticationHeader(request));
          }
        }),
        catchError((err) => this.handleError(err))
      );
    }
  }

  private addAuthenticationHeader(request: HttpRequest<any>) {
    const access_token = localStorage.getItem("access_token");
    const tenant_token = request.headers.has('Tenant-token') ? request.headers.get('Tenant-token') : localStorage.getItem("tenant_token");
    const tenant_country_code = request.headers.has('Tenant-country-code') ? request.headers.get('Tenant-country-code') : localStorage.getItem("tenant_country_code");
    const profile_code = localStorage.getItem("profileCode");
    if ((request.url.includes("/api/") || request.url.includes("/oauth/logout")) && access_token) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${access_token}`,
          locale:
            localStorage.getItem("locale") !== null
              ? localStorage.getItem("locale")
              : "pt-pt",
          timezone: this.addTimeZone(),
          ...(tenant_token &&
            profile_code === UserProfileCodes.ADMINISTRATOR && {
              "Tenant-Token": tenant_token,
            }),
          ...(tenant_country_code &&
            profile_code === UserProfileCodes.ADMINISTRATOR && {
              "Tenant-Country-Code": tenant_country_code,
            })
        },
      });
    }
    return request;
  }

  private logout() {
    this.store.dispatch(MessagesActionTypes.resetMessageFlag());
    localStorage.clear();
    this.dialogRef.closeAll();
    this.router.navigateByUrl("/login");
  }

  private addTimeZone(): string {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timeZone ? timeZone : 'UTC';
  }
}
